import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * Modern stat card component with glassmorphism effect
 * Used in DashboardV2 for displaying metrics
 * Supports theme colors: green, purple, blue
 */
const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  trend = null, // 'up', 'down', 'neutral'
  trendValue = null,
  colorTheme = 'green', // 'green', 'purple', 'blue'
  size = 'default' // 'default', 'large'
}) => {
  // Theme-aware accent colors
  const themeColors = {
    green: {
      bg: 'bg-emerald-500/10 dark:bg-emerald-500/20',
      icon: 'text-emerald-500 dark:text-emerald-400',
      border: 'border-emerald-500/20 dark:border-emerald-500/30',
      glow: 'shadow-emerald-500/5'
    },
    purple: {
      bg: 'bg-purple-500/10 dark:bg-purple-500/20',
      icon: 'text-purple-500 dark:text-purple-400',
      border: 'border-purple-500/20 dark:border-purple-500/30',
      glow: 'shadow-purple-500/5'
    },
    blue: {
      bg: 'bg-blue-500/10 dark:bg-blue-500/20',
      icon: 'text-blue-500 dark:text-blue-400',
      border: 'border-blue-500/20 dark:border-blue-500/30',
      glow: 'shadow-blue-500/5'
    }
  };

  const colors = themeColors[colorTheme] || themeColors.green;

  const trendConfig = {
    up: { icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    down: { icon: TrendingDown, color: 'text-rose-500', bg: 'bg-rose-500/10' },
    neutral: { icon: Minus, color: 'text-gray-500', bg: 'bg-gray-500/10' }
  };

  const TrendIcon = trend ? trendConfig[trend]?.icon : null;

  return (
    <div 
      className={`
        relative overflow-hidden rounded-2xl 
        bg-white/70 dark:bg-slate-800/50 
        backdrop-blur-xl 
        border ${colors.border}
        shadow-lg ${colors.glow}
        hover:shadow-xl hover:scale-[1.02]
        transition-all duration-300 ease-out
        ${size === 'large' ? 'p-6' : 'p-5'}
      `}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent dark:from-white/5 pointer-events-none" />
      
      <div className="relative flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
            {title}
          </p>
          <p className={`font-bold text-slate-900 dark:text-white ${size === 'large' ? 'text-4xl' : 'text-3xl'}`}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          
          {/* Trend indicator */}
          {trend && trendValue && (
            <div className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full ${trendConfig[trend]?.bg}`}>
              {TrendIcon && <TrendIcon className={`w-3 h-3 ${trendConfig[trend]?.color}`} />}
              <span className={`text-xs font-medium ${trendConfig[trend]?.color}`}>
                {trendValue}
              </span>
            </div>
          )}
        </div>
        
        {/* Icon container */}
        {Icon && (
          <div className={`${colors.bg} rounded-xl p-3`}>
            <Icon className={`w-6 h-6 ${colors.icon}`} />
          </div>
        )}
      </div>
    </div>
  );
};

export default StatCard;
